import clsx from 'clsx'
import type { ReactNode } from 'react'
import { Platform, Pressable, View } from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'

import { GradientTypography } from '@/src/elements/text/gradient'
import { Typography } from '@/src/elements/text/Typography'
import { useAccessibilityContext } from '@/src/features/accessibility/context/AccessibilityContext'
import { TEST_ID } from '@/src/general/constants/testId'

import { Badge } from '../../badge'
import { Icon } from '../../media/Icon'
import type { TTabBarProps } from './Tab.types'

const iconColor = (isActive?: boolean) =>
  isActive ? 'text-primary-icon-contrast-lower' : 'text-default-icon-contrast-lower'

export const TAB_BAR_HEIGHT = 60 as const

const computeBadgeContent = (count?: number) => {
  if (!count) return
  if (count > 9) return '+9'
  return count
}

export const TabBar = ({ navigation, activePage, navigationData, className }: TTabBarProps): ReactNode => {
  const { initializeRef } = useAccessibilityContext()
  const { bottom } = useSafeAreaInsets()

  return (
    <View
      role="tablist"
      // Using the full bottom inset would make the tab bar too tall.
      style={{ height: Platform.select({ default: TAB_BAR_HEIGHT + bottom / 2, web: TAB_BAR_HEIGHT }) }}
      className={clsx('native:border-t-1 native:border-gray-200 shadow-line flex-row', className)}
      testID={TEST_ID.NAVIGATION.TAB}
    >
      {Object.keys(navigationData).map((key, index) => {
        const route = { name: key, key }
        const { icon, hasNotifications, text, notificationsCount } = navigationData[route.name]
        return (
          <Pressable
            ref={(ref) => {
              if (index === 0) initializeRef(ref, 'navigation')
            }}
            key={route.key}
            className="native:justify-start native:py-2 m-1 flex-1 items-center justify-center gap-0.5 web:h-full"
            onPress={() => navigationData[route.name].onNavigate(navigation, route)}
            // Note: react-native-web doesn't support enter key on role="link" yet
            // and role button creates a bug and triggers onPress when focused
          >
            <Badge
              testID={`${key}-badge`}
              isInvisible={!hasNotifications}
              content={computeBadgeContent(notificationsCount)}
              showOutline
              variant="solid"
              size={notificationsCount && notificationsCount > 0 ? 'md' : 'lg'}
              isDot={!notificationsCount || notificationsCount < 1}
            >
              <Icon className={iconColor(route.name === activePage)} size="md" name={icon} />
            </Badge>

            {route.name === activePage && (
              <GradientTypography
                size="xs"
                colors="primary"
                classNameWrapper="native:h-10 w-full"
                className="native:w-full text-ellipsis text-center"
                numberOfLines={1}
              >
                {text}
              </GradientTypography>
            )}
            {route.name !== activePage && (
              <Typography size="xs" colors="default" contrast="low" className="text-ellipsis" numberOfLines={1}>
                {text}
              </Typography>
            )}
          </Pressable>
        )
      })}
    </View>
  )
}
